/**
 * Paragraph anchors: the `data-note` attribute that ties a manuscript paragraph to its note row.
 *
 * The anchor rides inside the chapter HTML, which is serialized straight out of the editor and
 * stored verbatim, so it survives text edits, reordering, and version restore for free. What it
 * does not survive is the handful of editor paths that clone or rebuild blocks — dedupeAnchors is
 * the repair for those, and must be called from every path that reaches onChangeContent.
 */
import { createId } from './id'

/** Kept identical to StoryCanvas's own block selector; that file imports this constant. */
export const MANUSCRIPT_PARAGRAPH_SELECTOR = 'p, div, blockquote'

export const NOTE_ANCHOR_ATTRIBUTE = 'data-note'

export function createAnchorId(): string {
  return createId()
}

export function anchorIdOf(element: HTMLElement): string | null {
  return element.getAttribute(NOTE_ANCHOR_ATTRIBUTE)
}

const normalizeText = (text: string): string => text.replace(/\s+/g, ' ').trim()

const isLeafBlock = (element: HTMLElement): boolean =>
  element.matches(MANUSCRIPT_PARAGRAPH_SELECTOR) &&
  !element.querySelector(MANUSCRIPT_PARAGRAPH_SELECTOR)

/**
 * Whether a single element is a block a note can attach to: a leaf block (one that contains no
 * other block, matching MANUSCRIPT_PARAGRAPH_SELECTOR itself) with text in it. Deliberately
 * without the minimap's `length > 50` filter — a note on a one-line exchange is a legitimate use,
 * and dialogue is where notes cluster.
 *
 * `li` and `h1`-`h6` are excluded: they match neither this selector nor fixFormatting, so an
 * anchor on one would be maintained by nothing. A bare top-level text node (the first line of a
 * chapter typed from scratch) has no element to carry an attribute and is likewise skipped —
 * normalizing it into a block would rewrite the author's HTML behind their back.
 *
 * Kept as the single definition both scanAnchorableBlocks (the sweep) and any single-element
 * check (e.g. the margin's hover handler, deciding whether a block may receive a NEW anchor) must
 * use — a block that fails this predicate has no element `measure()` will ever find again, so an
 * anchor stamped on one is invisible in the margin forever and unrepairable by dedupeAnchors
 * (which only collapses *duplicate* occurrences, not a lone anchor on the wrong kind of block).
 */
export function isAnchorableBlock(element: HTMLElement): boolean {
  if (!element.matches(MANUSCRIPT_PARAGRAPH_SELECTOR)) return false
  if (!normalizeText(element.textContent || '')) return false
  return !element.querySelector(MANUSCRIPT_PARAGRAPH_SELECTOR)
}

/** Every block a note can attach to — see isAnchorableBlock for the exact rule. */
export function scanAnchorableBlocks(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(MANUSCRIPT_PARAGRAPH_SELECTOR)).filter(
    isAnchorableBlock
  )
}

export function collectAnchorIds(root: HTMLElement): string[] {
  return scanAnchorableBlocks(root)
    .map(anchorIdOf)
    .filter((id): id is string => id !== null)
}

/**
 * Collapses duplicate anchors, keeping the block that actually holds the note's prose.
 *
 * Duplicates come from three places, none of which fire an `input` event: Enter mid-paragraph
 * (the browser copies attributes onto the new block), "Tidy up" (fixFormatting clones every
 * attribute onto each split sibling, in bulk), and any path that reassigns innerHTML.
 *
 * The tie-break is first-with-text, not first-in-document-order. Pressing Enter at offset 0
 * yields `<p data-note="a"><br></p><p data-note="a">Alpha…</p>` — plain first-wins would leave
 * the note pointing at an empty paragraph while its prose sits, unannotated, right below.
 *
 * Returns whether anything changed, so callers can skip a redundant save.
 */
export function dedupeAnchors(root: HTMLElement): boolean {
  const blocksByAnchor = new Map<string, HTMLElement[]>()
  for (const element of Array.from(
    root.querySelectorAll<HTMLElement>(`[${NOTE_ANCHOR_ATTRIBUTE}]`)
  )) {
    const anchorId = anchorIdOf(element)
    if (!anchorId) continue
    const existing = blocksByAnchor.get(anchorId)
    if (existing) existing.push(element)
    else blocksByAnchor.set(anchorId, [element])
  }

  let changed = false
  for (const blocks of blocksByAnchor.values()) {
    if (blocks.length < 2) continue
    // A wrapper that merely contains the anchored paragraph must never win: scanAnchorableBlocks
    // skips wrappers, so an anchor left on one is invisible to every accessor here — the note
    // survives in the HTML and detaches from the manuscript. Leaf-with-text first, then any leaf,
    // then whatever is left so an anchor is never dropped entirely.
    const keeper =
      blocks.find(
        (element) => isLeafBlock(element) && normalizeText(element.textContent || '') !== ''
      ) ??
      blocks.find(isLeafBlock) ??
      blocks[0]
    for (const element of blocks) {
      if (element === keeper) continue
      element.removeAttribute(NOTE_ANCHOR_ATTRIBUTE)
      changed = true
    }
  }
  return changed
}

/**
 * A key over chapter HTML that changes when the manuscript's BLOCK STRUCTURE or ANCHOR SET changes
 * — and stays byte-identical when only the prose does.
 *
 * This is what the margin overlay keys its measurement pass on. Keying on the content itself
 * (design spec §6.4 forbids it) put a full re-measure on every character: a new ResizeObserver, a
 * fresh `document.fonts.ready` registration, and one forced layout read per anchored block, on top
 * of the sweep the paragraph minimap already runs on the same cadence. Nothing a plain character
 * edit does can move a marker relative to its own paragraph; only splitting, merging, deleting or
 * annotating a block can, and both of those show up here.
 *
 * Deliberately a string scan rather than a DOM walk, so it costs no layout read at all and can sit
 * in a `useMemo` on the typing path. Reflow that structure alone cannot see — a paragraph rewrapping
 * onto another line and pushing everything below it down — is caught by the overlay's own
 * ResizeObserver on the editor element instead.
 */
export function anchorStructureKey(html: string): string {
  // One entry per block-level opening tag, in document order, holding that block's anchor id (or
  // '' for an unannotated block). That single pass carries all three signals at once: the block
  // COUNT is the entry count, the anchor SET is the non-empty entries, and their POSITIONS come
  // free — which matters, because an anchor moving from one paragraph to another (a version
  // restore, an undo) leaves both the count and the set identical while every marker has moved.
  //
  // Every block-level opening tag is counted, including wrappers scanAnchorableBlocks would skip:
  // this does not need to agree with that filter, only to change when the structure does, and an
  // over-count is just as stable across character edits. Text a writer types is serialized escaped
  // (`&lt;p`), so prose can never forge a block tag here.
  const blocks: string[] = []
  for (const match of html.matchAll(/<(?:p|div|blockquote)\b([^>]*)>/gi)) {
    blocks.push(/\sdata-note="([^"]*)"/i.exec(match[1])?.[1] ?? '')
  }
  return blocks.join(',')
}

/** Mirrors the server's stripNoteAnchors, for the publish path and any client-side comparison. */
export function stripNoteAnchors(html: string): string {
  return html.replace(/\s+data-note="[^"]*"/gi, '')
}
